import type { ComponentType, ReactElement } from 'react';
import { useRef } from 'react';

import {
    Container,
    LaneContainer,
    Lane,
    LaneLoader,
    EmptyStateMsg,
    ViewAll,
    StyledLink,
    StyledIcon,
    HeaderContainer,
} from './styled';

import { useValueBasedOnWindowSize } from '../../hooks/useValueBasedOnWindowSize';
import { useWindowSize } from '../../hooks/useWindowSize';
import { IconName } from '../Icon';

interface IOverviewSection<TData = unknown> {
    data: TData[];
    isLoading: boolean;
    type: string;
    sectionRoute: string;
    renderLaneCard: (data: TData) => ReactElement;
    emptyStateElement?: ReactElement;
    Title: ComponentType;
    count?: Number;
}

export function OverviewSection<TData = unknown>({
    data,
    renderLaneCard,
    isLoading,
    type,
    sectionRoute,
    emptyStateElement,
    Title,
    count,
}: IOverviewSection<TData>): ReactElement {
    const containerRef = useRef<HTMLDivElement>(null);
    const { width } = useWindowSize();
    const { left } = containerRef.current?.getBoundingClientRect() ?? {
        left: 0,
    };
    const [nrOfCards] = useValueBasedOnWindowSize<number>({
        0: 5,
        1440: 6,
        2200: 8,
    });
    const reducedData = data.slice(0, nrOfCards);

    return (
        <Container ref={containerRef}>
            <HeaderContainer>
                <Title />
            </HeaderContainer>
            <LaneContainer maxWidth={Math.round(width - left)}>
                <Lane>
                    {isLoading && <LaneLoader />}
                    {!isLoading &&
                        !reducedData.length &&
                        (emptyStateElement || (
                            <EmptyStateMsg>{`You don’t have any ${type}`}</EmptyStateMsg>
                        ))}
                    {reducedData.map((data) => renderLaneCard(data))}
                </Lane>
            </LaneContainer>

            <ViewAll>
                <StyledLink to={sectionRoute}>
                    View all {count?.toLocaleString('en-US')}
                    <StyledIcon iconName={IconName.ARROW} />
                </StyledLink>
            </ViewAll>
        </Container>
    );
}

OverviewSection.defaultProps = {
    emptyStateElement: null,
    count: null,
};
